import {ErrorMessage} from 'formik';
import {useTranslation} from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  SxProps,
  Typography,
} from '@mui/material';

import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';

interface SurveyOptionsProps {
  formik: any;
  isReadOnly?: boolean;
}

export const SurveyOptions = ({formik, isReadOnly}: SurveyOptionsProps) => {
  const {t} = useTranslation();

  const omitQuestionOptions = [
    {fieldName: 'omitDepartment', label: t('companySurveys.modals.omitDepartment')},
    {fieldName: 'omitSpokenLanguage', label: t('companySurveys.modals.omitSpokenLanguage')},
    {fieldName: 'omitGender', label: t('companySurveys.modals.omitGender')},
    {fieldName: 'omitLocation', label: t('companySurveys.modals.omitLocation')},
    {fieldName: 'omitAgeGroup', label: t('companySurveys.modals.omitAgeGroup')},
    {fieldName: 'omitWorkMode', label: t('companySurveys.modals.omitWorkMode')},
    {fieldName: 'omitFeedback', label: t('companySurveys.modals.omitFeedback')},
  ];
  const readOnlyControlStyles: SxProps = isReadOnly
    ? {
      '&.Mui-disabled': {
        color: greenColors[700],
        opacity: 1,
      },
      '&.Mui-checked.Mui-disabled': {
        color: greenColors[700],
      },
    }
    : {};
  const readOnlyLabelStyles = {
    color: greenColors[700],
    opacity: isReadOnly ? 0.8 : 1,
  };

  return (
    <FormControl
      sx={{
        width: '100%',
        backgroundColor: isReadOnly ? orangeColors[7] : 'transparent',
        border: isReadOnly ? `1px solid ${orangeColors[3]}` : 'none',
        borderRadius: isReadOnly ? '12px' : 0,
        padding: isReadOnly ? 2 : 0,
      }}
    >
      {isReadOnly ? (
        <Box
          sx={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            backgroundColor: 'white',
            border: `0.5px solid ${orangeColors[3]}`,
            borderRadius: '8px',
            color: greenColors[700],
            display: 'inline-flex',
            gap: '6px',
            marginBottom: 2,
            padding: '4px 10px',
          }}
        >
          <LockOutlinedIcon sx={{fontSize: 14}} />
          <Typography sx={{color: 'inherit', fontSize: '12px', lineHeight: 1.4}}>
            {t('companySurveys.modals.readOnly')}
          </Typography>
        </Box>
      ) : null}
      <RadioGroup
        name='defaultLanguage'
        row
        sx={{
          display: {xs: 'block', sm: 'flex'},
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Typography
          sx={{
            color: greenColors[700],
            mr: 2,
          }}
          variant='body1'
        >
          {t('companySurveys.modals.defaultLanguage')}:
        </Typography>

        <FormControlLabel
          control={
            <Radio
              onChange={(event) => {
                formik.setFieldValue('defaultLanguage', event.target.value);
              }}
              value={'de'}
              disabled={isReadOnly}
              checked={formik.values.defaultLanguage === 'de'}
              sx={readOnlyControlStyles}
              icon={
                <CircleIcon
                  fontSize='small'
                  sx={{
                    color: greenColors[700],
                  }}
                />
              }
              checkedIcon={
                <CheckCircleIcon
                  fontSize='small'
                  sx={{
                    color: greenColors[700],
                  }}
                />
              }
            />
          }
          label={
            <Typography
              sx={{
                ...readOnlyLabelStyles,
              }}
              variant='body1'
            >
              {t('shared.languages.de')}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Radio
              onChange={(event) => {
                formik.setFieldValue('defaultLanguage', event.target.value);
              }}
              disabled={isReadOnly}
              value={'en'}
              checked={formik.values.defaultLanguage === 'en'}
              sx={readOnlyControlStyles}
              icon={
                <CircleIcon
                  fontSize='small'
                  sx={{
                    color: greenColors[700],
                  }}
                />
              }
              checkedIcon={
                <CheckCircleIcon
                  fontSize='small'
                  sx={{
                    color: greenColors[700],
                  }}
                />
              }
            />
          }
          label={
            <Typography
              sx={{
                ...readOnlyLabelStyles,
              }}
              variant='body1'
            >
              {t('shared.languages.en')}
            </Typography>
          }
        />
      </RadioGroup>
      <Typography variant='body2' color='error'>
        <ErrorMessage name={'defaultLanguage'} component='span' />
      </Typography>
      <Box
        component='fieldset'
        sx={{
          mt: 3,
          px: 2,
          py: 1.5,
          backgroundColor: isReadOnly ? 'white' : 'transparent',
          border: `1px solid ${isReadOnly ? orangeColors[3] : greenColors[100]}`,
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography
          component='legend'
          sx={{
            color: greenColors[700],
            px: 1,
          }}
          variant='h4'
        >
          {t('companySurveys.modals.questionnaireConfiguration')}
        </Typography>
        <FormGroup>
          {omitQuestionOptions.map(({fieldName, label}) => (
            <FormControlLabel
              key={fieldName}
              name={fieldName}
              control={
                <Checkbox
                  onChange={(event) => {
                    formik.setFieldValue(fieldName, event.target.checked);
                  }}
                  checked={Boolean(formik.values[fieldName])}
                  disabled={isReadOnly}
                  value={true}
                  sx={{
                    color: greenColors[700],
                    '&.Mui-checked': {color: greenColors[700]},
                    ...readOnlyControlStyles,
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    ...readOnlyLabelStyles,
                  }}
                  variant='body1'
                >
                  {label}
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </Box>
    </FormControl>
  );
};
